import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Trash2, BookOpen, ChevronDown, Quote } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/utils/api';

export const OP_STATUS = {
  todo: ['À faire', 'bg-slate-100 text-slate-700'], in_progress: ['En cours', 'bg-blue-100 text-blue-700'],
  waiting_parts: ['En attente de pièces', 'bg-amber-100 text-amber-800'], waiting_approval: ['En attente d\'accord client', 'bg-orange-100 text-orange-800'],
  done: ['Terminé', 'bg-emerald-100 text-emerald-700'], cancelled: ['Annulé', 'bg-red-100 text-red-700 line-through'],
};
const APPROVAL = { not_needed: 'Accord non requis', pending: 'Accord demandé', approved: 'Accord donné', refused: 'Refusé' };

export const PartTitle = ({ n, title, hint }) => (
  <div className="flex items-baseline gap-3 mb-3">
    <span className="w-7 h-7 rounded-full bg-[#0E1116] text-[#FACC15] text-sm font-bold flex items-center justify-center shrink-0">{n}</span>
    <div><h2 className="text-base font-bold">{title}</h2>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
  </div>
);

export const InitialRequest = ({ workOrder, appointment }) => {
  const r = workOrder.request || {};
  const labels = r.motif_labels?.length ? r.motif_labels : [appointment?.intervention_type].filter(Boolean);
  const words = r.client_request || appointment?.description || workOrder.description || '';
  return (
    <Card className="p-4 rounded-xl border-2 border-[#FACC15] bg-[#FACC15]/5" data-testid="wo-initial-request">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Demande initiale (réception)</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {labels.map(l => <span key={l} className="px-2.5 py-1 rounded-full bg-[#0E1116] text-[#FACC15] text-xs font-bold" data-testid="wo-request-motif">{l}</span>)}
        {(r.precisions || []).map(p => <span key={p} className="px-2.5 py-1 rounded-full border border-slate-300 text-xs">{p}</span>)}
        {labels.length === 0 && <span className="text-sm text-slate-500">Aucun motif indiqué</span>}
      </div>
      {words && <p className="text-sm italic flex gap-2" data-testid="wo-request-words"><Quote className="w-4 h-4 text-slate-400 shrink-0" />{words}</p>}
      <p className="text-[11px] text-slate-500 mt-2">Motif déclaré par le client : ce n'est ni un diagnostic confirmé, ni une réparation autorisée, ni une ligne facturée.</p>
    </Card>
  );
};

export const DiagnosticPart = ({ woId, workOrder, children }) => {
  const [d, setD] = useState({ findings: '', measures: '', fault_codes: '', research: '', ...(workOrder.diagnostic || {}) });
  const [dirty, setDirty] = useState(false);
  const set = (k) => (e) => { setD(x => ({ ...x, [k]: e.target.value })); setDirty(true); };
  const save = async () => {
    try { await axios.put(`${API}/work-orders/${woId}/workshop-sheet`, { diagnostic: d }); setDirty(false); toast.success('Diagnostic enregistré'); }
    catch { toast.error('Erreur'); }
  };
  const field = (k, label, ph) => <div><p className="text-xs font-semibold text-slate-600 mb-1">{label}</p><Textarea rows={2} value={d[k]} onChange={set(k)} placeholder={ph} data-testid={`wo-diag-${k}`} /></div>;
  return (
    <Card className="p-4 rounded-xl" data-testid="wo-part-diagnostic">
      <PartTitle n="1" title="Contrôles et diagnostic" hint="Constats, mesures, codes défauts, photos et recherches" />
      <div className="grid md:grid-cols-2 gap-3">
        {field('findings', 'Constats du mécanicien', 'Ex : plaquettes AV à 2 mm')}
        {field('measures', 'Mesures', 'Ex : profondeur pneus AV 3 mm')}
        {field('fault_codes', 'Codes défauts', 'Ex : P0301')}
        {field('research', 'Recherches effectuées', 'Ex : test bobines, essai routier')}
      </div>
      {dirty && <Button size="sm" className="mt-3" onClick={save} data-testid="wo-diag-save-btn">Enregistrer le diagnostic</Button>}
      <div className="mt-4">{children}</div>
    </Card>
  );
};

const CatalogDialog = ({ open, onClose, catalog, onAdd }) => {
  const [q, setQ] = useState('');
  const [openGroups, setOpenGroups] = useState({});
  const motifLabel = Object.fromEntries(catalog.motifs.map(m => [m.key, m.label]));
  const filtered = catalog.operations.filter(o => !q || o.name.toLowerCase().includes(q.toLowerCase()));
  const groups = filtered.reduce((acc, o) => { ((acc[o.group] = acc[o.group] || {})[o.sub] = acc[o.group][o.sub] || []).push(o); return acc; }, {});
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="catalog-dialog">
        <DialogHeader><DialogTitle>Catalogue des opérations</DialogTitle></DialogHeader>
        <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input autoFocus value={q} onChange={e => setQ(e.target.value)} className="pl-9" placeholder="Rechercher une opération…" data-testid="catalog-search" /></div>
        <div className="space-y-1">
          {Object.entries(groups).map(([g, subs]) => {
            const isOpen = !!q || openGroups[g];
            return (
              <div key={g} className="border rounded-lg" data-testid={`catalog-group-${g}`}>
                <button type="button" className="w-full flex items-center justify-between px-3 py-2 font-semibold text-sm" onClick={() => setOpenGroups(s => ({ ...s, [g]: !s[g] }))} data-testid={`catalog-group-toggle-${g}`}>
                  {motifLabel[g] || g}<ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && Object.entries(subs).map(([sub, ops]) => (
                  <div key={sub} className="px-3 pb-2">
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-1">{sub}</p>
                    {ops.map(o => <button key={o.id} type="button" onClick={() => onAdd(o)} className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2" data-testid={`catalog-op-${o.id}`}><Plus className="w-3.5 h-3.5 text-slate-400" />{o.name}</button>)}
                  </div>
                ))}
              </div>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-slate-500 py-4">Aucune opération trouvée — ajoutez-la en texte libre.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const OpRow = ({ woId, op, onChanged }) => {
  const [o, setO] = useState(op);
  useEffect(() => setO(op), [op]);
  const save = async (patch) => {
    const next = { ...o, ...patch };
    setO(next);
    try { await axios.put(`${API}/work-orders/${woId}/operations/${op.id}`, next); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const del = async () => { if (!window.confirm('Supprimer cette opération ?')) return; await axios.delete(`${API}/work-orders/${woId}/operations/${op.id}`); onChanged(); };
  return (
    <div className="p-3 rounded-lg border space-y-2" data-testid={`wo-op-${op.id}`}>
      <div className="flex items-center gap-2">
        <span className={`flex-1 text-sm font-semibold ${o.status === 'cancelled' ? 'line-through text-slate-400' : ''}`}>{o.name}</span>
        <select value={o.status} onChange={e => save({ status: e.target.value, ...(e.target.value === 'done' && !o.parts_used && o.parts_needed ? { parts_used: o.parts_needed } : {}) })} className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${OP_STATUS[o.status][1]}`} data-testid={`wo-op-status-${op.id}`}>
          {Object.entries(OP_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" onClick={del} className="text-slate-400 hover:text-red-600" data-testid={`wo-op-delete-${op.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <Input className="h-8 text-sm" value={o.parts_needed} onChange={e => setO({ ...o, parts_needed: e.target.value })} onBlur={() => o.parts_needed !== op.parts_needed && save({})} placeholder="Pièces nécessaires" data-testid={`wo-op-parts-${op.id}`} />
        <select value={o.client_approval} onChange={e => save({ client_approval: e.target.value })} className="h-8 text-sm rounded-md border px-2" data-testid={`wo-op-approval-${op.id}`}>
          {Object.entries(APPROVAL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
    </div>
  );
};

export const OperationsPart = ({ woId, workOrder, onChanged }) => {
  const [catalog, setCatalog] = useState({ motifs: [], operations: [] });
  const [show, setShow] = useState(false);
  const [free, setFree] = useState('');
  useEffect(() => { axios.get(`${API}/workshop/catalog`).then(r => setCatalog(r.data)).catch(() => {}); }, []);
  const ops = workOrder.operations || [];
  const motifs = workOrder.request?.motifs || [];
  const suggestions = useMemo(() => motifs.flatMap(m => catalog.operations.filter(o => o.group === m && !ops.some(x => x.name === o.name)).slice(0, Math.max(3, Math.floor(12 / Math.max(motifs.length, 1))))), [catalog, motifs, ops]);
  const add = async (name, extra = {}) => {
    if (!name.trim()) return;
    try { await axios.post(`${API}/work-orders/${woId}/operations`, { name: name.trim(), ...extra }); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const pending = ops.filter(o => o.status !== 'done' && o.status !== 'cancelled');
  return (
    <Card className="p-4 rounded-xl" data-testid="wo-part-operations">
      <PartTitle n="2" title="Travaux à effectuer" hint="Opérations détaillées, pièces nécessaires et accord client" />
      {suggestions.length > 0 && (
        <div className="mb-3" data-testid="wo-op-suggestions">
          <p className="text-xs text-slate-500 mb-1">Suggestions selon le motif :</p>
          <div className="flex flex-wrap gap-1.5">{suggestions.map(o => (
            <button key={o.id} type="button" onClick={() => add(o.name, { catalog_id: o.id, motif: o.group })} className="text-xs px-2.5 py-1 rounded-full border border-dashed border-slate-400 hover:border-slate-900 hover:bg-slate-50" data-testid={`wo-op-suggest-${o.id}`}>+ {o.name}</button>
          ))}</div>
        </div>
      )}
      <div className="space-y-2">{pending.map(op => <OpRow key={op.id} woId={woId} op={op} onChanged={onChanged} />)}
        {pending.length === 0 && <p className="text-sm text-slate-500">Aucune opération en cours.</p>}</div>
      <div className="flex gap-2 mt-3">
        <Input value={free} onChange={e => setFree(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { add(free); setFree(''); } }} placeholder="Ajouter une opération en texte libre" data-testid="wo-op-free-input" />
        <Button variant="outline" onClick={() => { add(free); setFree(''); }} data-testid="wo-op-free-add-btn"><Plus className="w-4 h-4" /></Button>
        <Button onClick={() => setShow(true)} className="bg-[#0E1116] text-[#FACC15] hover:bg-black" data-testid="wo-catalog-btn"><BookOpen className="w-4 h-4 mr-1" />Catalogue</Button>
      </div>
      <CatalogDialog open={show} onClose={() => setShow(false)} catalog={catalog} onAdd={(o) => add(o.name, { catalog_id: o.id, motif: o.group })} />
    </Card>
  );
};
